import { useApiWorker } from 'hooks/useApiWorker';
import { LOGIN_WITH_API } from 'navigator/links';
import { UserProfile } from 'types/user';
import { storeUserInformation } from 'utilities/AuthService';

type LoginResponse = {
  loginSuccessful: boolean;
  errorMessage?: string;
};

type LogoutResponse = {
  logoutSuccessful: boolean;
  errorMessage?: string;
};

export async function SendUserToIdp(): Promise<void> {
  const idpUrl = import.meta.env.VITE_APP_IDP_LOGIN_URL || '';
  const clientId = import.meta.env.VITE_APP_AUTH_CLIENT_ID || '';
  const redirectUri = getRedirectUri();

  const state = [...Array(30)].map(() => Math.random().toString(36)[2]).join(''); // "random" str to identify a request to prevent XSS
  const audience = import.meta.env.VITE_APP_RULES_ENGINE_API_URL || '';

  const authUrl = `${idpUrl}?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}&audience=${audience}&scope=openid%20profile%20email`;
  window.location.href = authUrl;
}

export async function ExchangeAuthCodeForJwt(authCode: string): Promise<LoginResponse> {
  const apiWorker = useApiWorker();

  return apiWorker
    .post(`/loginWithIdp?authCode=${authCode}`)
    .then(() => {
      return GetUserInformation()
      .then(() => {
          return { loginSuccessful: true };
        })
        .catch((error) => {
          console.error('User information error:', error);
          return {
            loginSuccessful: false,
            errorMessage: error.response?.data || 'Error getting user information in login',
          }
        })
    })
    .catch((error) => {
      console.error('Login error:', error);
      return {
        loginSuccessful: false,
        errorMessage: error.response?.data || 'Error in login',
      };
    });
}

export async function RefreshToken(): Promise<LoginResponse> {
  const apiWorker = useApiWorker();

  return apiWorker
    .post<string>(`/refreshToken`)
    .then(() => {
        return GetUserInformation()
        .then(() => {
          return { loginSuccessful: true };
        })
        .catch((error) => {
          console.error('User information error:', error);
          return {
            loginSuccessful: false,
            errorMessage: error.response?.data || 'Error getting user information in token refresh',
          }
        })
    })
    .catch((error) => {
      console.error('Refresh error:', error);
      return {
        loginSuccessful: false,
        errorMessage: error.response?.data || 'Error in token refresh',
      };
    });
}

export async function GetUserInformation(): Promise<LoginResponse> {
  const apiWorker = useApiWorker();

  return apiWorker
    .get<UserProfile>(`/userinfo`)
    .then((result) => {
      const userProfile: UserProfile = result.data;
      storeUserInformation(userProfile)
      return { loginSuccessful: true };
    })
    .catch((error) => {
      return {
        loginSuccessful: false,
        errorMessage: `Failed to get user data.`
      };
    });
}

export async function Logout(): Promise<LogoutResponse> {
  const apiWorker = useApiWorker();

  return apiWorker
    .post<string>(`/logout`)
    .then((result) => {
      if (result && result.status == 200) {
        return {
          logoutSuccessful: true
        };
      } else {
        return {
          logoutSuccessful: false,
          errorMessage: 'Error during logout process.'
        };
      }
    })
    .catch((error) => {
      console.error('Logout request error:', error);
      return {
        logoutSuccessful: false,
        errorMessage: 'Error making logout request.'
      };
    });
}

export async function GetHealthCheckData(): Promise<any> {
  const apiWorker = useApiWorker();

  return apiWorker
    .get<any>(`/health`)
    .then((result) => {
      const healthCheckData = result.data;
      return { 
        healthCheckRequestStatus: 'SUCCESS',
        errorMessage: '',
        healthCheckData: healthCheckData
       };
    })
    .catch((error) => {
      return {
        healthCheckRequestStatus: 'FAILED TO MAKE HEALTH CHECK',
        errorMessage: error.toString(),        
        healthCheckData: null
      };
    });
}

function getRedirectUri() {
  const uiHostUrl = getStringWithNoTrailingOrLeadingSlashed(import.meta.env.VITE_APP_THIS_UI_HOST);
  const baseUrl = getStringWithNoTrailingOrLeadingSlashed(import.meta.env.VITE_APP_BASE_PATH);
  const loginApi = getStringWithNoTrailingOrLeadingSlashed(LOGIN_WITH_API);
  return `${uiHostUrl}/${baseUrl}/${loginApi}`;
}

function getStringWithNoTrailingOrLeadingSlashed(str: string) {
  return str.replace(/\/+$/, '').replace(/^\/+/, '');
}
